import { DolsStatus, ResidentItem, ResidentStatus } from '@lmc/ts-common'
import { Tag } from '@lmc/ui-kit'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { EditTooltip } from './EditTooltip'

type ProfileBadgesProps = {
  resident: ResidentItem
  testId?: string
}

const canShowDolsBadge = (
  resident: ResidentItem,
  dolsModsAlignment: boolean
) => {
  const dolsEndDate = resident.dolsEndDate ? dayjs(resident.dolsEndDate) : null
  const dolsEndDateValid = !dolsEndDate || dolsEndDate.isAfter(dayjs())

  const displayDolsBadge = dolsModsAlignment
    ? resident.dolsStatus === DolsStatus.authorised && dolsEndDateValid
    : resident.dols && dolsEndDateValid

  return displayDolsBadge
}

export const ProfileBadges = ({ resident, testId }: ProfileBadgesProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { isDoLSOverride } = useHomeType()
  const dolsModsAlignment = useFeatureFlags({
    flagNames: ['dols_mods_alignment']
  }).data?.dols_mods_alignment.enabled

  const displayDolsBadge = canShowDolsBadge(resident, dolsModsAlignment)

  const hasAllergies = resident.residentAllergies?.length > 0

  const hasNoKnownAllergies = resident?.noKnownAllergies === true

  const isDummy = resident.status === ResidentStatus.dummy

  return (
    <StyledBadges
      data-testid={testId ? `${testId}-profile-badges` : 'profile-badges'}
    >
      {isDummy && (
        <Tag
          data-testid={
            testId ? `${testId}-dummy-tag` : 'profile-badges-dummy-tag'
          }
        >
          {t(supConstants.DUMMY)}
        </Tag>
      )}
      {(hasAllergies || hasNoKnownAllergies) && (
        <Tag
          color="green"
          data-testid={
            testId ? `${testId}-allergies-tag` : 'profile-badges-allergies-tag'
          }
        >
          {t(
            supConstants[
              hasNoKnownAllergies ? 'NO_KNOWN_ALLERGIES' : 'ALLERGIES'
            ]
          ).toUpperCase()}
        </Tag>
      )}
      {resident.dnacpr && (
        <Tag
          color="red"
          data-testid={
            testId ? `${testId}-dnacpr-tag` : 'profile-badges-dnacpr-tag'
          }
        >
          {t(supConstants.DNACPR).toUpperCase()}
        </Tag>
      )}
      {resident.hasChokingRisk && (
        <Tag
          color="orange"
          data-testid={
            testId
              ? `${testId}-choking-risk-tag`
              : 'profile-badges-choking-risk-tag'
          }
        >
          {t(supConstants.CHOKING_RISK).toUpperCase()}
        </Tag>
      )}
      {displayDolsBadge && (
        <EditTooltip
          title={t(supConstants.DOLS_ENDING_TOOLTIP, {
            date: dayjs(resident.dolsEndDate).format('DD/MM/YYYY')
          })}
        >
          <Tag
            color="blue"
            data-testid={
              testId ? `${testId}-dols-tag` : 'profile-badges-dols-tag'
            }
          >
            {isDoLSOverride ? t(supConstants.DOLS) : t(supConstants.LPS)}
          </Tag>
        </EditTooltip>
      )}
    </StyledBadges>
  )
}

const StyledBadges = styled.div`
  display: flex;
  padding-top: 8px;
  height: fit-content;
`
